import { type ReactNode, useEffect, useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BatchProvider } from './BatchContext';
import { router } from './router';
import { setUnauthorizedHandler } from '@/lib/api-client';

/**
 * App-wide providers:
 *  - QueryClientProvider: TanStack Query (real fetching is off in Phase 1).
 *  - BatchProvider: global selected-batch state, persisted in localStorage.
 *
 * I18nProvider and ThemeProvider are intentionally not wrapped here:
 *  - i18next configures itself at module-load time (src/i18n/index.ts).
 *  - Theme is applied to <html> before render (src/main.tsx).
 *  - A Toaster will be added when the toast primitive is generated.
 */
interface AppProvidersProps {
  children: ReactNode;
}

export const AppProviders = ({ children }: AppProvidersProps) => {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            // Phase 1: no real endpoints yet. Disable retries to avoid 404 loops
            // and require explicit `enabled: true` per call site.
            retry: false,
            staleTime: 60_000,
            refetchOnWindowFocus: false,
          },
        },
      }),
  );

  // Bounce to /login whenever a request 401s mid-session (expired cookie).
  // Clears the cached user + all queries so the login screen starts clean.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      queryClient.setQueryData(['auth', 'me'], null);
      queryClient.removeQueries();
      if (router.state.location.pathname !== '/login') {
        void router.navigate('/login', { replace: true });
      }
    });
    return () => setUnauthorizedHandler(null);
  }, [queryClient]);

  return (
    <QueryClientProvider client={queryClient}>
      <BatchProvider>{children}</BatchProvider>
    </QueryClientProvider>
  );
};
